// src/ReportPage.js
import React, { Component } from "react";
import {
  Box,
  Stack,
  Paper,
  Button,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import axios from "axios";
import Header from "./Header";
import UserMenu from "./UserMenu";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DownloadIcon from "@mui/icons-material/Download";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { stateFipsToName, normalizeId } from "./utils/idUtils";
import { FIPS_TO_ABBR, SEATS_BY_STATE, buildDistrictName } from "./utils/districtNames";
import DistrictReportView from "./DistrictReportView";
import InvariantDistrictReportView from "./InvariantDistrictReportView";
import StackExampleClientReport from "./StackExampleClientReport";

/* -------------------------------------------------------------------------- */
/* STATIC LOOKUPS                                                              */
/* -------------------------------------------------------------------------- */

function StepLabel({ number, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          border: "1px solid #94a3b8",
          color: "#334155",
          fontSize: 11,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#475569" }}>
        {label}
      </Typography>
    </Stack>
  );
}

/* -------------------------------------------------------------------------- */
/* PDF HELPER (same as UK version)                                             */
/* -------------------------------------------------------------------------- */

async function waitForImages(rootEl) {
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return;
      return new Promise((res) => {
        img.addEventListener("load", res, { once: true });
        img.addEventListener("error", res, { once: true });
      });
    })
  );
}

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT                                                              */
/* -------------------------------------------------------------------------- */

export default class ReportPage extends Component {
  state = {
    generating: false,
    selState: "",
    selDistrict: "",
    selAreaType: "cd",
    selSeat: "",
    seatOptions: [],
    seatLoading: false,
    // committed items queued for preview + export
    // each item: { id, fips, stateAbbr, district, districtName, areaCode }
    selectedDistricts: [],
    userReady: false,
    user: { email: "", name: "", company: "" },
    allowedSections: [],
    clientAccess: "default",
    assignedClientAccess: "default",
    reportType: "political",
    demo:false
  };

  getDemoQueueKey = () => {
    const email = (this.state.user?.email || this.props.user?.email || "demo").toLowerCase().trim();
    return `district_demo_report_queue:${email}`;
  };

  loadDemoQueue = () => {
    const isDemoUser = this.state.clientAccess === "demo" || this.state.demo;
    if (!isDemoUser) return;
    try {
      const raw = window.localStorage.getItem(this.getDemoQueueKey()) || "[]";
      const parsed = JSON.parse(raw);
      const safeQueue = Array.isArray(parsed)
        ? parsed.filter((item) => item && item.id && item.fips && item.reportType).slice(0, 2)
        : [];
      this.setState({ selectedDistricts: safeQueue });
    } catch {
      this.setState({ selectedDistricts: [] });
    }
  };

  persistDemoQueue = () => {
    const isDemoUser = this.state.clientAccess === "demo" || this.state.demo;
    if (!isDemoUser) return;
    try {
      const queue = (this.state.selectedDistricts || []).slice(0, 2);
      window.localStorage.setItem(this.getDemoQueueKey(), JSON.stringify(queue));
    } catch {}
  };

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.reportType !== this.state.reportType &&
      (this.state.reportType === "client" || this.state.reportType === "example")
    ) {
      if (this.state.selAreaType === "cd") {
        this.setState({ selAreaType: "statesenate", selDistrict: "", selSeat: "" });
      }
    }
    if (
      (prevState.clientAccess !== this.state.clientAccess || prevState.user?.email !== this.state.user?.email) &&
      (this.state.clientAccess === "demo" || this.state.demo)
    ) {
      this.loadDemoQueue();
    }
    if (
      prevState.selectedDistricts !== this.state.selectedDistricts &&
      (this.state.clientAccess === "demo" || this.state.demo)
    ) {
      this.persistDemoQueue();
    }
  }

  componentDidMount() {
    const CLIENT_ACCESS = {
      default: [],
      stack: ["pol", "healthcare", "stateleg", "ai", "gamble"],
      hanbury: ["pol", "healthcare", "ai", "stateleg", "gamble"],
      phrma: ["phrma"],
      acc: ["acc_politics", "acc_policy", "acc_climate"],
      pmi: ["pmi", "pol", "stateleg", "healthcare", "environ", "consumer"],
      demo: ["demo-pol", "demo-health"],
      intuit: ["pol", "intuit"],
      invariant: ["pol", "invariant_industry", "invariant_message", "invariant_h2h"],
    };
    const normalizeClientAccess = (val) => {
      if (val === "demo-pol" || val === "demo-health") return "demo";
      return val || "default";
    };

    if (this.props.assignedClientAccess) {
      const assigned = normalizeClientAccess(this.props.assignedClientAccess);
      const allowedSections = CLIENT_ACCESS[assigned] || [];
      this.setState({
        user: this.props.user || { email: "", name: "", company: "" },
        allowedSections,
        userReady: true,
        clientAccess: assigned,
        assignedClientAccess: assigned,
        reportType: assigned === "invariant" ? "client" : "political",
        demo: assigned === "demo",
      }, () => {
        if (assigned === "demo") this.loadDemoQueue();
      });
      return;
    }

    axios
      .get("/api/user-info/")
      .then((response) => {
        const user = response.data || {};
        const email = user.email || "";
        const domain = email.split("@")[1]?.toLowerCase() || "";
        const isDemoUser = user.is_demo_user === true;
        const companySlug = user.company_slug || null;
        if (!user || Object.keys(user).length === 0 || !email) {
          window.location.href = "/";
          return;
        }

        // Resolve client identity from slug first, then fall back to email domain
        const slugToClient = {
          "stack-data-strategy": "stack",
          "hanbury": "hanbury",
          "phrma": "phrma",
          "acc": "acc",
          "pmi": "pmi",
          "intuit": "intuit",
          "invariant": "invariant",
          "demo": "demo",
        };

        let assigned = "default";
        if (isDemoUser || companySlug === "demo") {
          assigned = "demo";
        } else if (companySlug && slugToClient[companySlug]) {
          assigned = slugToClient[companySlug];
        } else {
          // Domain fallback
          if (domain.includes("stackdatastrategy.demo")) assigned = "demo";
          else if (domain.includes("phrma.org")) assigned = "phrma";
          else if (domain.includes("pmi.org")) assigned = "pmi";
          else if (domain.includes("hanburystrategy.com")) assigned = "hanbury";
          else if (domain.includes("accaction.eco")) assigned = "acc";
          else if (domain.includes("stackdatastrategy.com")) assigned = "stack";
          else if (domain.includes("leavittdc.com")) assigned = "intuit";
          else if (domain.includes("weareinvariant.com")) assigned = "invariant";
          else if (domain.includes("personalcarecouncil.org")) assigned = "invariant";
        }

        const allowedSections = [...CLIENT_ACCESS.default, ...(CLIENT_ACCESS[assigned] || [])];

        if (assigned === "demo") {
          this.setState({demo:true})
        }

        this.setState({
          user,
          allowedSections,
          userReady: true,
          clientAccess: assigned,
          assignedClientAccess: assigned,
          reportType: assigned === "invariant" ? "client" : "political",
        }, () => {
          if (assigned === "demo") this.loadDemoQueue();
        });
      })
      .catch(() => {
        window.location.href = "/";
      });
  }

  /* ── Selector helpers ── */

  handleStateChange = (e) => {
    const selState = e.target.value;
    this.setState({ selState, selDistrict: "", selSeat: "" }, () => {
      this.loadSeatOptionsIfNeeded();
    });
  };

  handleDistrictChange = (e) => {
    this.setState({ selDistrict: e.target.value });
  };

  handleAreaTypeChange = (e) => {
    const next = e.target.value;
    this.setState({ selAreaType: next, selDistrict: "", selSeat: "" }, () => {
      this.loadSeatOptionsIfNeeded();
    });
  };

  handleSeatChange = (e) => {
    this.setState({ selSeat: e.target.value });
  };

  loadSeatOptionsIfNeeded = () => {
    const { reportType, selAreaType, selState } = this.state;
    if (reportType !== "client" && reportType !== "example") {
      this.setState({ seatOptions: [], seatLoading: false, selSeat: "" });
      return;
    }
    if (!selState || selAreaType === "cd") {
      this.setState({ seatOptions: [], seatLoading: false, selSeat: "" });
      return;
    }
    this.loadSeatOptions(selAreaType, selState);
  };

  loadSeatOptions = async (level, stateFips) => {
    try {
      this.setState({ seatLoading: true, seatOptions: [] });
      const url =
        level === "statesenate"
          ? process.env.PUBLIC_URL + "/usmaps/us_statelegsenate_simplified.json"
          : process.env.PUBLIC_URL + "/usmaps/us_stateleghouse_simplified.json";
      const res = await fetch(url);
      const data = await res.json();
      let features = data.features || [];
      const stateField =
        features.length && "STATE" in features[0].properties ? "STATE" : "STATEFP";
      const padded = String(stateFips).padStart(2, "0");
      features = features.filter((f) => String(f.properties?.[stateField]) === padded);
      const opts = features
        .map((f) => {
          const props = f.properties || {};
          const areaId = normalizeId(level, props);
          const name =
            props.NAME ||
            props.NAMELSAD ||
            `District ${props.SLDUST || props.SLDLST || ""}`.trim();
          const idDigits = String(areaId ?? "").replace(/\\D/g, "");
          const fromId = idDigits.length >= 3 ? Number(idDigits.slice(-3)) : null;
          const match = String(name).match(/\\b(\\d+)\\b/);
          const fromName = match ? Number(match[1]) : null;
          const num = Number.isFinite(fromId) ? fromId : fromName;
          return { value: areaId, label: name, num };
        })
        .sort((a, b) => {
          if (a.num != null && b.num != null) return a.num - b.num;
          if (a.num != null) return -1;
          if (b.num != null) return 1;
          return String(a.label).localeCompare(String(b.label));
        });
      this.setState({ seatOptions: opts.map(({ num, ...rest }) => rest), seatLoading: false });
    } catch {
      this.setState({ seatOptions: [], seatLoading: false });
    }
  };

  handleAddDistrict = () => {
    const { selState, selDistrict, selectedDistricts, reportType, selAreaType, selSeat, seatOptions } = this.state;
    const isDemoUser = this.state.clientAccess === "demo" || this.state.demo;
    if (isDemoUser && selectedDistricts.length >= 2) return;
    const isCD = reportType === "political" || selAreaType === "cd";
    const isState = selAreaType === "states";
    if (!selState || (isCD && !selDistrict) || (!isCD && !isState && !selSeat)) return;

    const stateAbbr = FIPS_TO_ABBR[selState] || selState;
    const stateName = stateFipsToName[selState] || selState;
    const seatLabel = !isCD && !isState
      ? (seatOptions.find((o) => o.value === selSeat)?.label || "")
      : "";
    const seatLabelNumMatch = seatLabel.match(/(\d+)/);
    const seatLabelNum = seatLabelNumMatch ? seatLabelNumMatch[1] : seatLabel;
    const districtName = isCD
      ? buildDistrictName(selState, selDistrict)
      : isState
      ? `${stateName}`
      : `${stateName} ${selAreaType === "statesenate" ? "State Senate" : "State House"} — District ${seatLabelNum}`;
    const areaCode = isCD ? `${stateAbbr}-${selDistrict}` : (isState ? selState : selSeat);
    const id = `${stateAbbr}-${isCD ? selDistrict : (isState ? "STATE" : selSeat)}-${selectedDistricts.length}`;

    const alreadyAdded = selectedDistricts.some(
      (d) => d.fips === selState && d.district === (isCD ? selDistrict : (isState ? selState : selSeat)) && d.reportType === reportType && d.areaType === (isCD ? "cd" : selAreaType)
    );
    if (alreadyAdded) return;

    this.setState({
      selectedDistricts: [
        ...selectedDistricts,
        { id, fips: selState, stateAbbr, stateName, district: isCD ? selDistrict : (isState ? null : selSeat), districtName, areaCode, reportType, areaType: isCD ? "cd" : selAreaType },
      ],
    });
  };

  removeDistrict = (id) => {
    const isDemoUser = this.state.clientAccess === "demo" || this.state.demo;
    if (isDemoUser) return;
    this.setState((prev) => ({
      selectedDistricts: prev.selectedDistricts.filter((d) => d.id !== id),
    }));
  };

  clearAll = () => {
    const isDemoUser = this.state.clientAccess === "demo" || this.state.demo;
    if (isDemoUser) return;
    this.setState({ selectedDistricts: [] });
  };

  handleReportTypeChange = (_e, nextType) => {
    if (!nextType) return;
    const canUseClient = this.state.assignedClientAccess === "invariant";
    const canUseExample = this.state.clientAccess === "stack" && !this.state.demo;
    if (nextType === "client" && !canUseClient) return;
    if (nextType === "example" && !canUseExample) return;
    this.setState({ reportType: nextType });
  };

  /* ── PDF export (mirrors UK generatePdfFor exactly) ── */

  generatePdfFor = async (item) => {
    this.setState({ generating: true });

    const element = document.getElementById(`report-content-${item.id}`);
    if (!element) {
      console.error("❌ Could not find report container for", item.id);
      this.setState({ generating: false });
      return;
    }

    await waitForImages(element);

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");

    const imgWidth = 595;
    const pageHeight = 842;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const prefix =
      item.reportType === "example"
        ? "ExampleClient"
        : item.reportType === "client"
        ? "Invariant"
        : "Political";
    const safeName = item.districtName
      .replace(/\s+/g, "_")
      .replace(/[—–-]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
    pdf.save(`${prefix}_${safeName}_DistrictProfile.pdf`);
    this.setState({ generating: false });
  };

  generateAll = async () => {
    for (const item of this.state.selectedDistricts) {
      await this.generatePdfFor(item);
    }
  };

  /* ── Render ── */

  render() {
    const {
      generating,
      selState,
      selDistrict,
      selAreaType,
      selSeat,
      seatOptions,
      seatLoading,
      selectedDistricts,
      assignedClientAccess,
      reportType,
    } = this.state;

    const CLIENT_OPTIONS = [
      { value: "default", label: "Default" },
      { value: "stack", label: "Stack" },
      { value: "hanbury", label: "Hanbury" },
      { value: "phrma", label: "PhRMA" },
      { value: "pmi", label: "PMI" },
      { value: "acc", label: "ACC" },
      { value: "intuit", label: "Intuit" },
      { value: "invariant", label: "Invariant" },
      { value: "demo", label: "Demo" },
    ];
    const assignedClientLabel =
      CLIENT_OPTIONS.find((o) => o.value === assignedClientAccess)?.label || "Default";
    const clientEnabled = assignedClientAccess === "invariant";
    const isDemoUser = this.state.clientAccess === "demo" || this.state.demo;
    const exampleEnabled = this.state.clientAccess === "stack" && !isDemoUser;
    const demoLimit = 2;
    const demoRemaining = Math.max(0, demoLimit - selectedDistricts.length);

    const THEME_OPTIONS = [
      { value: "pol", label: "Political" },
      { value: "healthcare", label: "Health" },
      { value: "environ", label: "Environment" },
      { value: "consumer", label: "Consumer" },
      { value: "phrma", label: "Client Portal" },
      { value: "pmi", label: "PMI" },
      { value: "stateleg", label: "State Legistlative" },
      { value: "ai", label: "Tech" },
      { value: "acc_politics", label: "Politics" },
      { value: "acc_policy", label: "Policy" },
      { value: "acc_climate", label: "Climate" },
      { value: "gamble", label: "Prediction Markets" },
      { value: "demo-pol", label: "Political" },
      { value: "demo-health", label: "Healthcare" },
    ];

    if (!this.state.userReady) {
      return (
        <Box sx={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: "50%",
            border: "4px solid rgba(0,0,0,0.12)",
            borderTopColor: "#2563eb",
            animation: "spin 0.9s linear infinite",
          }} />
        </Box>
      );
    }

    const showHeader = this.props.showHeader !== false;
    const visibleThemes = showHeader
      ? THEME_OPTIONS.filter((opt) => this.state.allowedSections?.includes(opt.value))
      : [];

    const seatCount = SEATS_BY_STATE[selState] || 53;
    const districtOptions =
      seatCount === 1
        ? [{ value: "00", label: "At-Large" }]
        : Array.from({ length: seatCount }, (_, i) => {
            const d = String(i + 1).padStart(2, "0");
            return { value: d, label: `District ${d}` };
          });



    const isCD = reportType === "political" || selAreaType === "cd";
    const isState = selAreaType === "states";
    const canAdd =
      !!selState &&
      (isCD ? !!selDistrict : isState ? true : !!selSeat) &&
      (!isDemoUser || demoRemaining > 0) &&
      !selectedDistricts.some(
        (d) =>
          d.fips === selState &&
          d.district === (isCD ? selDistrict : (isState ? selState : selSeat)) &&
          d.reportType === reportType &&
          d.areaType === (isCD ? "cd" : selAreaType)
      );

    const reportTypeCards = [
      {
        value: "political",
        title: "Political",
        desc: "Congressional district political brief.",
        icon: <AccountBalanceRoundedIcon sx={{ fontSize: 14 }} />,
        iconBg: "#dbeafe",
        iconColor: "#1d4ed8",
        locked: false,
      },
      ...(exampleEnabled && !clientEnabled
        ? [{
            value: "example",
            title: "Example Client",
            desc: "Stack-branded sample client briefing.",
            icon: <ApartmentRoundedIcon sx={{ fontSize: 14 }} />,
            iconBg: "#f3e8ff",
            iconColor: "#7c3aed",
            locked: false,
          }]
        : [{
            value: "client",
            title: "Client",
            desc: clientEnabled ? "Invariant state and district briefing." : "Full version",
            icon: <ApartmentRoundedIcon sx={{ fontSize: 14 }} />,
            iconBg: "#dcfce7",
            iconColor: "#166534",
            locked: !clientEnabled,
          }]
      ),
    ];

    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f7f8fa" }}>
        {showHeader && (
          <Header
            visibleThemes={visibleThemes}
            selectedSection={null}
            clientAccess={this.state.clientAccess}
            setThemeInUrl={(theme) => (window.location.href = `/${theme}`)}
            UserMenu={UserMenu}
            user={this.state.user}
            onLogout={this.handleLogout}
            onTableauOpen={this.toggleTableau}
            showReportTab
            reportActive
            onReportOpen={() => {}}
          />
        )}

        <Box sx={{ display: "flex", flex: 1, p: 2, gap: 2 }}>

        {/* ── LEFT SIDEBAR ── */}
        <Box
          sx={{
            width: 372,
            flexShrink: 0,
            bgcolor: "background.paper",
            border: "0.5px solid",
            borderColor: "divider",
            borderRadius: 3,
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            height: "90vh",
            overflow: "hidden",
            boxShadow: "0 12px 32px rgba(15,23,42,0.06)",
          }}
        >
          <Box sx={{ px: 2.5, py: 2.25, borderBottom: "0.5px solid", borderColor: "divider" }}>
            <Typography variant="subtitle1" fontWeight={500}>Report studio</Typography>
            <Typography variant="caption" color="text.secondary">
              Build and export district reports
            </Typography>
            {isDemoUser && (
              <Box
                sx={{
                  mt: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: "1px solid rgba(37,99,235,0.25)",
                  bgcolor: "rgba(37,99,235,0.06)",
                }}
              >
                <Typography sx={{ fontSize: "11px", color: "#1e3a8a", fontWeight: 500 }}>
                  Demo mode: create up to 2 reports.
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ px: 3, py: 2.75, height: "calc(90vh - 78px)", overflowY: "auto", display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <StepLabel number={1} label="Choose report type" />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Select the report you want to add.
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.1 }}>
                {reportTypeCards.map((cfg) => {
                  const isSelected = reportType === cfg.value;
                  return (
                    <Paper
                      key={cfg.value}
                      variant="outlined"
                      onClick={() => !cfg.locked && this.handleReportTypeChange(null, cfg.value)}
                      sx={{
                        p: 1.4,
                        cursor: cfg.locked ? "default" : "pointer",
                        borderRadius: 2,
                        border: isSelected ? "1.5px solid #111827" : "0.5px solid",
                        borderColor: isSelected ? "#111827" : "divider",
                        bgcolor: isSelected ? "#f8fafc" : "background.paper",
                        opacity: cfg.locked ? 0.55 : 1,
                        position: "relative",
                        transition: "all 0.16s ease",
                        boxShadow: isSelected ? "0 6px 18px rgba(15,23,42,0.08)" : "none",
                      }}
                    >
                      {cfg.locked && (
                        <Box sx={{ position: "absolute", top: 5, right: 5 }}>
                          <LockOutlinedIcon sx={{ fontSize: 10, color: "text.disabled" }} />
                        </Box>
                      )}
                      <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: cfg.iconBg, color: cfg.iconColor, display: "flex", alignItems: "center", justifyContent: "center", mb: 0.75 }}>
                        {cfg.icon}
                      </Box>
                      <Typography variant="caption" fontWeight={500} display="block" sx={{ fontSize: "10px" }}>
                        {cfg.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: "9px" }}>
                        {cfg.desc}
                      </Typography>
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <StepLabel number={2} label="Select geography" />
              <Select value={selState} onChange={this.handleStateChange} displayEmpty size="small" fullWidth sx={{ mt: 0.5 }}>
                <MenuItem value=""><em>Select a state</em></MenuItem>
                {Object.entries(stateFipsToName).sort((a, b) => a[1].localeCompare(b[1])).map(([fips, name]) => (
                  <MenuItem key={fips} value={fips}>{name}</MenuItem>
                ))}
              </Select>

              {(reportType === "client" || reportType === "example") && (
                <Select value={selAreaType} onChange={this.handleAreaTypeChange} size="small" fullWidth sx={{ mt: 1 }}>
                  <MenuItem value="states">State</MenuItem>
                  <MenuItem value="statesenate">State Senate</MenuItem>
                  <MenuItem value="statehouse">State House</MenuItem>
                  <MenuItem value="cd">US House (CD)</MenuItem>
                </Select>
              )}

              {reportType === "client" && (
                <Paper variant="outlined" sx={{ mt: 1, px: 1.25, py: 0.9, borderRadius: 1.5, bgcolor: "#f8fafc", borderColor: "#e2e8f0" }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "text.primary" }}>
                    {assignedClientLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Assigned client
                  </Typography>
                </Paper>
              )}

              {reportType === "example" && (
                <Paper variant="outlined" sx={{ mt: 1, px: 1.25, py: 0.9, borderRadius: 1.5, bgcolor: "#f8fafc", borderColor: "#e2e8f0" }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "text.primary" }}>
                    Example Client
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Stack-branded sample report
                  </Typography>
                </Paper>
              )}

              {isCD && (
                <Select value={selDistrict} onChange={this.handleDistrictChange} displayEmpty size="small" fullWidth disabled={!selState} sx={{ mt: 1 }}>
                  <MenuItem value="">
                    <em>{selState ? "Select a district" : "Select a state first"}</em>
                  </MenuItem>
                  {districtOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              )}

              {!isCD && (reportType === "client" || reportType === "example") && (selAreaType === "statesenate" || selAreaType === "statehouse") && (
                <Select value={selSeat} onChange={this.handleSeatChange} displayEmpty size="small" fullWidth disabled={!selState || seatLoading} sx={{ mt: 1 }}>
                  <MenuItem value="">
                    <em>{selState ? (seatLoading ? "Loading seats…" : "Select a seat") : "Select a state first"}</em>
                  </MenuItem>
                  {seatOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              )}
            </Box>

            <Button
              fullWidth
              variant="contained"
              disabled={!canAdd}
              onClick={this.handleAddDistrict}
              startIcon={<CheckCircleRoundedIcon />}
              sx={{ bgcolor: "#111827", "&:hover": { bgcolor: "#0f172a" }, borderRadius: 2 }}
            >
              Add to report
            </Button>
            {isDemoUser && demoRemaining <= 0 && (
              <Typography variant="caption" color="text.secondary">
                Demo limit reached for this account.
              </Typography>
            )}

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" fontWeight={500}>Report queue</Typography>
                  <Chip
                    label={selectedDistricts.length}
                    size="small"
                    sx={{ height: 18, fontSize: 10, bgcolor: "#111827", color: "#fff", borderRadius: "999px", "& .MuiChip-label": { px: 1 } }}
                  />
                </Stack>
                <Button size="small" variant="text" onClick={this.clearAll} disabled={selectedDistricts.length === 0 || isDemoUser} sx={{ fontSize: 11, color: "text.secondary", minWidth: 0, px: 0.75 }}>
                  Clear all
                </Button>
              </Stack>

              {selectedDistricts.length > 0 ? (
                <Stack spacing={0.75}>
                  {selectedDistricts.map((item) => (
                    <Paper key={item.id} variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, bgcolor: item.reportType === "client" ? "#059669" : item.reportType === "example" ? "#7c3aed" : "#2563eb" }} />
                        <Typography variant="caption" fontWeight={500}>{item.districtName}</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Chip label={item.reportType === "client" ? "Client" : item.reportType === "example" ? "Example" : "Political"} size="small" sx={{ height: 18, fontSize: 10, borderRadius: "999px" }} />
                        {!isDemoUser && (
                          <IconButton size="small" onClick={() => this.removeDistrict(item.id)}>
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Search a geography and choose a report type to get started.
                </Typography>
              )}
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              fullWidth
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              disabled={generating || selectedDistricts.length === 0}
              onClick={this.generateAll}
            >
              {generating ? "Exporting…" : "Export All PDFs"}
            </Button>

            <Typography variant="caption" color="text.disabled">
              The on-screen preview is captured as-is into the PDF. Wait for all data to load before exporting.
            </Typography>
          </Box>
        </Box>

        {/* ── RIGHT PREVIEW PANE ── */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: "grey.50",
            p: 3,
            overflowY: "auto",
            scrollBehavior: "smooth",
          }}
        >
          {selectedDistricts.length > 0 ? (
            selectedDistricts.map((item) => (
              <Paper
              key={item.id}
             
        elevation={3}
        sx={{
          mx: "auto",
          mb: 4,
          p: 2.5,
          width: 840,
          minHeight: 1180,
          borderRadius: 3,
          scrollSnapAlign: "start",
        }}
      >
                {/* Per-card export button */}
                {/* <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 10,
                  }}
                >
                  <Tooltip title={`Export ${item.districtName} PDF`}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PictureAsPdfIcon />}
                      disabled={generating}
                      onClick={() => this.generatePdfFor(item)}
                      sx={{ fontSize: "0.7rem", py: 0.4 }}
                    >
                      PDF
                    </Button>
                  </Tooltip>
                </Box> */}

                {/* The report itself — id used by html2canvas */}
                {item.reportType === "political" ? (
                  <DistrictReportView
                    id={`report-content-${item.id}`}
                    districtName={item.districtName}
                    state={item.stateAbbr}
                    stateFips={item.fips}
                    stateName={item.stateName}
                    district={item.district != null ? Number(item.district) : null}
                    areaCode={item.areaCode}
                  />
                ) : item.reportType === "example" ? (
                  <StackExampleClientReport
                    id={`report-content-${item.id}`}
                    districtName={item.districtName}
                    state={item.stateAbbr}
                    stateFips={item.fips}
                    stateName={item.stateName}
                    district={item.district != null ? Number(item.district) : null}
                    areaCode={item.areaCode}
                    areaType={item.areaType}
                  />
                ) : (
                  <InvariantDistrictReportView
                    id={`report-content-${item.id}`}
                    districtName={item.districtName}
                    state={item.stateAbbr}
                    stateFips={item.fips}
                    stateName={item.stateName}
                    district={item.district != null ? Number(item.district) : null}
                    areaCode={item.areaCode}
                    areaType={item.areaType}
                    clientLabel={assignedClientLabel}
                  />
                )}
              </Paper>
            ))
          ) : (
            <Box
              sx={{ height: "100%", display: "grid", placeItems: "center", color: "text.secondary" }}
            >
              <Stack spacing={1} alignItems="center">
                <PictureAsPdfIcon fontSize="large" />
                <Typography variant="body1">No preview yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a state and district to render a district report.
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
    );
  }
}
